import React, { useState } from "react";
import { Comment, commentTypes } from "../../firebase/comments";
import TextField from "@material-ui/core/TextField";
import Button from "../shared/Button";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../firebase/AuthProvider";
import ProfileLink from "../shared/ProfileLink";
import Avatar from "../shared/Avatar";
import SignInDialog from "../signin/SigninDialog";
import CommentTypePicker from "./CommentTypePicker";
import Information from "../shared/Information";
import { colors } from "../../theme";

interface Props {
  save: (comment: Comment) => void;
}

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: "100%",
  },
  form: {
    marginTop: theme.spacing(3),
    display: "flex",
  },
  submit: {
    margin: theme.spacing(1, 0, 2),
  },
  button: {
    float: "right",
  },
  disabled: {
    backgroundColor: "#CCCCCC"
  },
  textfield: {
    width: "100%",
  },
  error: {
    color: colors.ERROR,
  },
}));

export default function CommentForm({ save }: Props) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { isSignedIn, user } = useAuth();
  const {
    register,
    handleSubmit,
    errors,
    formState: { dirty, isValid },
    reset,
    control,
  } = useForm<Comment>({
    mode: "onChange",
  });

  const [openSignInDialog, setOpenSignInDialog] = useState(false);
  const [confirmProposal, setConfirmProposal] = useState(false);

  const handleClose = () => {
    setOpenSignInDialog(false);
  };

  function onSubmit(comment: Comment, event: any) {
    save(comment);
    reset();
    if (comment.type === commentTypes.PROPOSAL) setConfirmProposal(true);
  }

  function checkSignedIn() {
    if (!isSignedIn && !openSignInDialog) setOpenSignInDialog(true);
  }

  return (
    <div className={classes.root}>
      <SignInDialog open={openSignInDialog} onClose={handleClose} />

      {confirmProposal && (
        <Information
          title={t("commentform.confirmproposaltitle")}
          text={t("commentform.confirmproposaltext")}
          handleOk={() => setConfirmProposal(false)}
        />
      )}

      <form
        className={classes.form}
        noValidate
        onSubmit={handleSubmit(onSubmit)}
      >
        <Grid container sm xs={12} spacing={2}>
          {user && (
            <Grid item xs={2} sm={2}>
              <ProfileLink userId={user.id}>
                <Avatar>{user.avatar}</Avatar>
              </ProfileLink>
            </Grid>
          )}
          <Grid item xs={10} sm={10} container>
            <TextField
              className={classes.textfield}
              label=""
              placeholder={t("commentform.placeholder")}
              multiline
              variant="outlined"
              name="text"
              inputRef={register({
                required: true,
                minLength: 1,
                maxLength: 500,
              })}
              error={errors.text && errors.text.type === "maxLength"}
              helperText={
                errors.text && errors.text.type === "maxLength"
                  ? t("commentform.error_maxlength", { max: 500 })
                  : null
              }
              onClick={checkSignedIn}
              onKeyDown={checkSignedIn}
            />

            <Grid item xs={12} sm container className={classes.submit}>
              <Grid item xs={12} sm={10} container>
                <Controller
                  as={CommentTypePicker}
                  name="type"
                  control={control}
                  onChange={([commentType]) => commentType}
                  defaultValue={commentTypes.COMMENT}
                />
              </Grid>
              <Grid item xs={12} sm={2} container>
                {!isSignedIn || !dirty || (dirty && !isValid) ? (
                  <Button
                    type="submit"
                    className={classes.button && classes.disabled}
                    disabled={true}
                  >
                    {t("commentform.submit")}
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    className={classes.button}
                  >
                    {t("commentform.submit")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </div>
  );
}
